import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, ArrowLeft, Users, Maximize2, Bed, Bath, Wifi, Coffee, Wind, Tv, Shield, Phone } from 'lucide-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const RoomCarousel = ({ images }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      setSelectedIndex(emblaApi.selectedScrollSnap());
    }
  }, [emblaApi]);

  return (
    <div className="room-details-carousel">
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {images.map((image, index) => (
            <div className="embla__slide" key={index}>
              <img src={image} alt={`Room view ${index + 1}`} className="room-details-carousel-image" />
            </div>
          ))}
        </div>
      </div>
      <button className="embla__prev" onClick={scrollPrev}>
        <ChevronLeft size={28} />
      </button>
      <button className="embla__next" onClick={scrollNext}>
        <ChevronRight size={28} />
      </button>
      <div className="carousel-indicators">
        {images.map((_, index) => (
          <button
            key={index}
            className={`carousel-indicator ${index === selectedIndex ? 'active' : ''}`}
            onClick={() => emblaApi?.scrollTo(index)}
          />
        ))}
      </div>
    </div>
  );
};

const RoomDetailsPage = () => {
  const { roomType } = useParams();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');

  const roomsData = {
    luxury: {
      name: "Luxury Room",
      price: 4000,
      images: [
        `${process.env.PUBLIC_URL + "/static/images/room1.jpeg"}`,
        `${process.env.PUBLIC_URL + "/static/images/room2.jpeg"}`,
        `${process.env.PUBLIC_URL + "/static/images/room3.jpeg"}`,
        `${process.env.PUBLIC_URL + "/static/images/room4.JPG"}`
      ],
      description: "Immerse yourself in unparalleled comfort with our Luxury Room. Designed with meticulous attention to detail, this room combines modern elegance with traditional warmth. Floor-to-ceiling windows offer breathtaking views of our scenic farmland, while premium furnishings and state-of-the-art amenities ensure your stay is nothing short of extraordinary.",
      maxGuests: 2,
      size: "350 sq ft",
      bedType: "King-size bed",
      features: [
        { icon: <Bed size={20} />, name: "King-size bed with premium linens" },
        { icon: <Bath size={20} />, name: "Luxury bathroom with rainfall shower" },
        { icon: <Wind size={20} />, name: "Climate control air conditioning" },
        { icon: <Tv size={20} />, name: "55\" Smart TV with streaming services" },
        { icon: <Wifi size={20} />, name: "High-speed WiFi (complimentary)" },
        { icon: <Coffee size={20} />, name: "Mini bar and coffee maker" },
        { icon: <Shield size={20} />, name: "Electronic safe" },
        { icon: <Phone size={20} />, name: "24/7 room service" }
      ],
      amenities: [
        "Private balcony with seating area",
        "Premium toiletries and bathrobes",
        "Daily housekeeping service",
        "Work desk with ergonomic chair",
        "Blackout curtains",
        "In-room dining available",
        "Complimentary bottled water",
        "Iron and ironing board"
      ],
      highlights: [
        "Stunning farmland views",
        "Soundproof windows",
        "Hypoallergenic bedding available",
        "Pet-friendly options available"
      ]
    },
    premium: {
      name: "Premium Room",
      price: 5000,
      images: [
        `${process.env.PUBLIC_URL + "/static/images/room1.jpeg"}`,
        `${process.env.PUBLIC_URL + "/static/images/room2.jpeg"}`,
        `${process.env.PUBLIC_URL + "/static/images/room3.jpeg"}`,
        `${process.env.PUBLIC_URL + "/static/images/room4.JPG"}`
      ],
      description: "Experience the pinnacle of luxury in our Premium Room. Spanning a generous space with an exclusive layout, this room is perfect for those seeking the ultimate in comfort and sophistication. Featuring a separate living area, spa-inspired bathroom, and personalized butler service, every moment here is crafted to perfection.",
      maxGuests: 3,
      size: "450 sq ft",
      bedType: "King-size bed",
      features: [
        { icon: <Bed size={20} />, name: "Luxury king-size bed with premium mattress" },
        { icon: <Bath size={20} />, name: "Spa bathroom with soaking tub & shower" },
        { icon: <Wind size={20} />, name: "Smart climate control system" },
        { icon: <Tv size={20} />, name: "65\" Smart TV with premium channels" },
        { icon: <Wifi size={20} />, name: "Ultra-high-speed WiFi" },
        { icon: <Coffee size={20} />, name: "Premium bar with espresso machine" },
        { icon: <Shield size={20} />, name: "Digital safe with charging port" },
        { icon: <Phone size={20} />, name: "Dedicated butler service" }
      ],
      amenities: [
        "Private terrace with panoramic views",
        "Luxury bath products and plush robes",
        "Turndown service twice daily",
        "Separate living and sleeping areas",
        "Smart home controls",
        "Complimentary minibar refresh daily",
        "Nespresso machine with premium pods",
        "Yoga mat and wellness amenities"
      ],
      highlights: [
        "Priority check-in/check-out",
        "Complimentary breakfast in room",
        "Access to executive lounge",
        "Personalized concierge service"
      ]
    }
  };

  const room = roomsData[roomType];

  if (!room) {
    return <div>Room not found</div>;
  }

  const handleBookNow = () => {
    navigate('/', { state: { scrollTo: 'booking', roomType: room.name } });
  };

  return (
    <div className="room-details-page">
      <Header activeSection={activeSection} setActiveSection={setActiveSection} />
      
      <div className="room-details-hero">
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Back
        </button>
      </div>

      <div className="room-details-container">
        <div className="room-details-header">
          <div>
            <h1 className="room-details-title">{room.name}</h1>
            <div className="room-details-specs">
              <span className="spec-item">
                <Maximize2 size={18} /> {room.size}
              </span>
              <span className="spec-item">
                <Users size={18} /> Up to {room.maxGuests} guests
              </span>
              <span className="spec-item">
                <Bed size={18} /> {room.bedType}
              </span>
            </div>
          </div>
          <div className="room-details-pricing">
            <span className="room-price-large">₹{room.price.toLocaleString()}</span>
            <span className="room-price-period">/night</span>
          </div>
        </div>

        <RoomCarousel images={room.images} />

        <div className="room-details-content">
          <div className="room-details-main">
            <section className="room-section">
              <h2 className="section-heading">About This Room</h2>
              <p className="room-description-detailed">{room.description}</p>
            </section>

            <section className="room-section">
              <h2 className="section-heading">Room Features</h2>
              <div className="features-grid-detailed">
                {room.features.map((feature, index) => (
                  <div key={index} className="feature-item-detailed">
                    <div className="feature-icon-detailed">{feature.icon}</div>
                    <span>{feature.name}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="room-section">
              <h2 className="section-heading">Amenities</h2>
              <div className="amenities-grid">
                {room.amenities.map((amenity, index) => (
                  <div key={index} className="amenity-item">
                    <Check size={18} className="amenity-check" />
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="room-section">
              <h2 className="section-heading">Room Highlights</h2>
              <div className="highlights-list">
                {room.highlights.map((highlight, index) => (
                  <div key={index} className="highlight-item">
                    <Check size={20} className="highlight-check" />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="room-details-sidebar">
            <div className="booking-card-sticky">
              <h3 className="booking-card-title">Reserve Your Stay</h3>
              <div className="booking-card-price">
                <span className="price-amount">₹{room.price.toLocaleString()}</span>
                <span className="price-unit">/night</span>
              </div>
              <ul className="booking-benefits-list">
                <li>Free cancellation within 24 hours</li>
                <li>Complimentary breakfast included</li>
                <li>Best price guarantee</li>
                <li>Free WiFi & parking</li>
              </ul>
              <Button onClick={handleBookNow} className="book-now-btn-large">
                Book This Room
              </Button>
              <p className="booking-note">You won't be charged yet</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RoomDetailsPage;
